import { ApolloError } from "@apollo/client";
import { Alert, Button } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import { SettingsResearchTypeSpeciesCreateInput } from "../..";
import { AddSpeciesDialog } from "./AddSpeciesDialog";
import { ResearchTypeSpeciesListCardFragment } from "./fragments.generated";
import ResearchTypeSpeciesDataGrid from "./ResearchTypeSpeciesDataGrid";

type Props = {
    researchTypeSpecies: ResearchTypeSpeciesListCardFragment[];
    researchTypeId: string;
    onAddSpecies: (data: SettingsResearchTypeSpeciesCreateInput) => Promise<void>;
    addSpeciesError?: ApolloError;
    onRemoveSpecies: (speciesId: string) => Promise<void>;
    onToggleShowToClientUser: (speciesId: string, value: boolean) => Promise<void>;
    removeSpeciesError?: ApolloError;
};

export default function ResearchTypeSpeciesListCard({
    researchTypeSpecies,
    researchTypeId,
    onAddSpecies,
    addSpeciesError,
    onRemoveSpecies,
    onToggleShowToClientUser,
    removeSpeciesError,
}: Props) {
    const { t } = useTranslation("researchType");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const [showSpeciesDialog, setShowSpeciesDialog] = useState(false);

    const handleAddSpecies = async (data: SettingsResearchTypeSpeciesCreateInput) => {
        await onAddSpecies(data);
        setShowSpeciesDialog(false);
    };

    return (
        <>
            {isAdmin && (
                <>
                    <Button onClick={() => setShowSpeciesDialog(true)}>
                        {t("researchTypeSpeciesListCard.actionAddSpecies")}
                    </Button>
                    <AddSpeciesDialog
                        error={addSpeciesError}
                        researchTypeId={researchTypeId}
                        visible={showSpeciesDialog}
                        onClose={() => setShowSpeciesDialog(false)}
                        onSubmitClick={handleAddSpecies}
                    />
                </>
            )}
            {removeSpeciesError !== undefined && <Alert severity="error">{removeSpeciesError.message}</Alert>}
            <ResearchTypeSpeciesDataGrid
                researchTypeSpecies={researchTypeSpecies}
                isAdmin={isAdmin}
                onDeleteClick={onRemoveSpecies}
                onToggleShowToClientUserClick={onToggleShowToClientUser}
            />
        </>
    );
}
